import React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCompressAlt } from '@rtkwlf/pro-solid-svg-icons/faCompressAlt';
import { faExpandArrows } from '@rtkwlf/pro-solid-svg-icons/faExpandArrows';
import { faPen } from '@rtkwlf/pro-solid-svg-icons/faPen';
import { faRedo } from '@rtkwlf/pro-solid-svg-icons/faRedo';
import { faTimes } from '@rtkwlf/pro-solid-svg-icons/faTimes';
import { useTranslation } from 'react-i18next';

import { Button, Stack, Typography } from '@packages/fenrir/components';
import { ComingSoon } from 'ae-components/coming-soon/coming-soon';

interface AssetsTableBulkActionsProps {
  count: number;
  onDeselectAll: VoidFunction;
  onDelete?: VoidFunction;
  onUpdate?: VoidFunction;
  onRescan?: VoidFunction;
  onMerge?: VoidFunction;
  onSplit?: VoidFunction;
}

export const AssetsTableBulkActions: React.FC<AssetsTableBulkActionsProps> = ({
  count,
  onDeselectAll,
  onDelete,
  onUpdate,
  onRescan,
  onMerge,
  onSplit,
}) => {
  const { t } = useTranslation('ads');

  if (count === 0) {
    return null;
  }

  return (
    <Stack
      direction="row"
      spacing={2}
      sx={{
        alignItems: 'center',
        backgroundColor: 'widget.widgetBackground',
        py: 1,
        px: 2,
        borderRadius: 0.125,
        width: 1,
      }}
      data-testid="asset-list-bulk-actions"
    >
      <Typography>
        {t('assetList.bulkActions.selectedAssets', {
          numAssets: count,
        })}
      </Typography>

      <ComingSoon featureFlagKey="ADS_FULL_MOCKED" flex={1}>
        <Stack
          direction="row"
          sx={{ justifyContent: 'space-between', flex: 1 }}
        >
          <Stack direction="row" spacing={2}>
            {onUpdate && (
              <Button
                color="primary"
                startIcon={<FontAwesomeIcon icon={faPen} />}
                variant="contained"
                onClick={onUpdate}
                disabled
              >
                {t('assetList.bulkActions.updateAssetDetails')}
              </Button>
            )}

            {onRescan && (
              <Button
                startIcon={<FontAwesomeIcon icon={faRedo} />}
                onClick={onRescan}
                variant="outlined"
                color="primary"
                disabled
              >
                {t('assetList.bulkActions.rescan')}
              </Button>
            )}

            {onMerge && (
              <Button
                startIcon={<FontAwesomeIcon icon={faCompressAlt} />}
                onClick={onMerge}
                variant="outlined"
                color="primary"
                disabled
              >
                {t('assetList.bulkActions.merge')}
              </Button>
            )}

            {onSplit && (
              <Button
                startIcon={<FontAwesomeIcon icon={faExpandArrows} />}
                onClick={onSplit}
                variant="outlined"
                color="primary"
                disabled
              >
                {t('assetList.bulkActions.split')}
              </Button>
            )}
          </Stack>
          <Stack direction="row">
            {onDelete && (
              <Button
                onClick={onDelete}
                variant="outlined"
                color="error"
                disabled
              >
                {t('assetList.bulkActions.deleteAssets')}
              </Button>
            )}
          </Stack>
        </Stack>
      </ComingSoon>

      <Button
        startIcon={<FontAwesomeIcon icon={faTimes} />}
        variant="outlined"
        onClick={onDeselectAll}
      >
        {t('assetList.bulkActions.deselectAll')}
      </Button>
    </Stack>
  );
};
